import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Button, 
  Input, 
  Label, 
  FormGroup,
  Alert,
  Spinner,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import config from '../../config';
import { RiMoneyEuroCircleLine, RiCheckLine, RiAlertLine, RiUserLine, RiExternalLinkLine } from 'react-icons/ri';
import { toast } from 'react-toastify';

interface Project {
  id: number;
  title: string;
  NUM?: string;
  num?: string;
}

interface QuickMoneySpotlightProps {
  isOpen: boolean;
  toggle: () => void;
}

const QuickMoneySpotlight: React.FC<QuickMoneySpotlightProps> = ({ isOpen, toggle }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'form' | 'disclaimer'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeTab, setActiveTab] = useState('self');
  
  // Form data
  const [formData, setFormData] = useState({
    amount: '',
    project_id: null as number | null,
    notes: '',
    external_email: '',
    external_name: ''
  });
  
  // Disclaimer acceptance
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [userName, setUserName] = useState('');
  const [userNameInput, setUserNameInput] = useState('');

  // Load projects
  useEffect(() => {
    if (isOpen) {
      loadProjects();
      loadUserName();
    }
  }, [isOpen]);

  const loadProjects = async () => {
    try {
      const authUser = sessionStorage.getItem('authUser');
      console.log('Auth user from storage:', authUser);
      if (!authUser) {
        console.log('No auth user found');
        return;
      }
      
      const parsed = JSON.parse(authUser);
      const token = parsed.token || parsed.data?.token;
      console.log('Token extracted:', token);
      
      const url = `${config.api.API_URL}/projects`;
      console.log('Loading projects from:', url);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          pageSize: 1000, 
          page: 1
          // Remove status filter to get all projects
        }
      });
      
      console.log('Projects response:', response.data);
      
      if (response.data && response.data.data) {
        setProjects(response.data.data);
        console.log('Loaded projects:', response.data.data.length);
        console.log('First project:', response.data.data[0]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
    }
  };

  const loadUserName = () => {
    // Get user from sessionStorage
    const authUser = sessionStorage.getItem("authUser");
    if (authUser) {
      try {
        const obj = JSON.parse(authUser);
        if (obj.user) {
          setUserName(obj.user.full_name || obj.user.username || obj.user.name || '');
        }
      } catch (error) {
        console.error('Error parsing auth user:', error);
      }
    }
  };

  const handleSubmitRequest = () => {
    // Validate form
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }
    if (amount > 5000) {
      setError('Der Maximalbetrag für Verrechnungsgeld beträgt 5.000 €');
      return;
    }
    
    // Additional validation for external
    if (activeTab === 'external') {
      if (!formData.external_email) {
        setError('Bitte geben Sie eine E-Mail-Adresse ein');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.external_email)) {
        setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        return;
      }
      if (!formData.external_name) {
        setError('Bitte geben Sie einen Namen ein');
        return;
      }
    }
    
    // Move to disclaimer step only for self
    setError(null);
    if (activeTab === 'self') {
      setStep('disclaimer');
    } else {
      handleCreateExternalRequest();
    }
  };

  const handleCreateExternalRequest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (!authUser) {
        setError('Nicht authentifiziert');
        return;
      }

      const parsed = JSON.parse(authUser);
      const token = parsed.token || parsed.data?.token;
      
      const response = await axios.post(
        `${config.api.API_URL}/external-expense/create`,
        {
          external_email: formData.external_email,
          external_name: formData.external_name,
          amount: parseFloat(formData.amount),
          project_id: formData.project_id,
          notes: formData.notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Externe Verrechnungsgeld-Anfrage erfolgreich erstellt');
        // Reset and close
        setFormData({ amount: '', project_id: null, notes: '', external_email: '', external_name: '' });
        setActiveTab('self');
        toggle();
        // Navigate to expense reports
        navigate('/expense-reports');
      } else {
        setError(response.data.message || 'Fehler beim Erstellen der externen Anfrage');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Fehler beim Erstellen der externen Anfrage');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDisclaimer = async () => {
    if (!disclaimerAccepted) {
      setError('Bitte akzeptieren Sie die Bedingungen');
      return;
    }
    
    if (userNameInput.toLowerCase() !== userName.toLowerCase()) {
      setError('Bitte geben Sie Ihren Namen korrekt ein zur Bestätigung');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (!authUser) {
        setError('Nicht authentifiziert');
        return;
      }
      
      const parsed = JSON.parse(authUser);
      const token = parsed.token || parsed.data?.token;
      
      const response = await axios.post(
        `${config.api.API_URL}/expense-reports/quick-money`,
        {
          amount: parseFloat(formData.amount),
          project_id: formData.project_id,
          notes: formData.notes,
          disclaimer_accepted: true,
          user_name: userName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        // Reset and close
        setFormData({ amount: '', project_id: null, notes: '', external_email: '', external_name: '' });
        setStep('form');
        setDisclaimerAccepted(false);
        setUserNameInput('');
        setActiveTab('self');
        toggle();
        
        // Navigate to expense reports
        navigate('/expense-reports');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Fehler beim Erstellen der Anfrage');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state immediately
    setStep('form');
    setFormData({ amount: '', project_id: null, notes: '', external_email: '', external_name: '' });
    setDisclaimerAccepted(false);
    setUserNameInput('');
    setError(null);
    setActiveTab('self');
    
    // Call toggle function directly
    if (toggle) {
      toggle();
    }
  };

  const returnDate = new Date();
  returnDate.setDate(returnDate.getDate() + 30);

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={handleClose} 
      size={step === 'disclaimer' ? 'lg' : 'md'}
      style={{ zIndex: 100000 }}
      backdrop={true}
      backdropClassName="quick-money-backdrop"
      modalClassName="quick-money-modal-foreground"
      keyboard={false}
    >
      <ModalHeader toggle={toggle}>
        <RiMoneyEuroCircleLine className="me-2" />
        {step === 'form' ? 'Verrechnungsgeld anfordern' : 'Rechtliche Vereinbarung'}
      </ModalHeader>
      
      <ModalBody>
        {error && <Alert color="danger" fade={false}>{error}</Alert>}
        
        {step === 'form' ? (
          <>
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === 'self' ? 'active' : ''}
                  onClick={() => setActiveTab('self')}
                  style={{ cursor: 'pointer' }}
                >
                  <RiUserLine className="me-2" />
                  Für mich selbst
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'external' ? 'active' : ''}
                  onClick={() => setActiveTab('external')}
                  style={{ cursor: 'pointer' }}
                >
                  <RiExternalLinkLine className="me-2" />
                  Für externen Nutzer
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="self">
                <FormGroup>
                  <Label>Betrag (EUR) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    max="5000"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    autoFocus
                  />
                  <small className="text-muted">Maximalbetrag: 5.000 €</small>
                </FormGroup>
              </TabPane>
              
              <TabPane tabId="external">
                <Alert color="info" className="mb-3">
                  Der externe Nutzer erhält eine E-Mail mit einem Link zur digitalen Unterschrift.
                </Alert>
                
                <FormGroup>
                  <Label>E-Mail-Adresse des externen Nutzers *</Label>
                  <Input
                    type="email"
                    value={formData.external_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, external_email: e.target.value }))}
                    placeholder="externe.person@example.com"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Name des externen Nutzers *</Label>
                  <Input
                    type="text"
                    value={formData.external_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, external_name: e.target.value }))}
                    placeholder="Max Mustermann"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Betrag (EUR) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    max="5000"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                  <small className="text-muted">Maximalbetrag: 5.000 €</small>
                </FormGroup>
              </TabPane>
            </TabContent>
            
            {/* Common fields for both tabs */}
            <FormGroup>
              <Label>Projekt</Label>
              <Select
                isClearable
                placeholder="Projekt auswählen (optional)"
                options={projects}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const num = option.NUM || option.num || '';
                  if (num && option.title) {
                    return `${num} - ${option.title}`;
                  }
                  return option.title || '';
                }}
                getOptionValue={(option) => option?.id?.toString() || ''}
                onChange={(option) => {
                  console.log('Project selected:', option);
                  setFormData(prev => ({ ...prev, project_id: option?.id || null }));
                }}
                value={projects.find(p => p.id === formData.project_id) || null}
                noOptionsMessage={() => projects.length === 0 ? 'Keine Projekte geladen' : 'Keine Projekte gefunden'}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Verwendungszweck</Label>
              <Input
                type="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Wofür wird das Geld benötigt? (optional)"
              />
            </FormGroup>
          </>
        ) : (
          <div className="disclaimer-content">
            <Card className="mb-3 border-warning">
              <CardBody>
                <h5 className="text-warning mb-3">
                  <RiAlertLine className="me-2" />
                  Wichtige Vereinbarung
                </h5>
                <div className="mb-3">
                  <strong>Betrag:</strong> {parseFloat(formData.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}<br />
                  <strong>Rückgabedatum:</strong> {returnDate.toLocaleDateString('de-DE')}<br />
                  {formData.project_id && (
                    <>
                      <strong>Projekt:</strong> {projects.find(p => p.id === formData.project_id)?.title}<br />
                    </>
                  )}
                  {formData.notes && (
                    <>
                      <strong>Verwendungszweck:</strong> {formData.notes}
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
            
            <div className="legal-text" style={{ maxHeight: '300px', overflowY: 'auto', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <h6>VEREINBARUNG ÜBER VERRECHNUNGSGELD</h6>
              
              <p>
                Hiermit bestätige ich, <strong>{userName}</strong>, dass ich Verrechnungsgeld in Höhe von
                <strong> {parseFloat(formData.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} </strong>
                von der MXR Productions GmbH erhalte.
              </p>
              
              <h6 className="mt-3">Ich verpflichte mich:</h6>
              <ol>
                <li>Das Verrechnungsgeld ausschließlich für geschäftliche Zwecke der MXR Productions GmbH zu verwenden.</li>
                <li>Alle Ausgaben mit ordnungsgemäßen Belegen (Rechnungen, Quittungen) zu dokumentieren.</li>
                <li>Die Belegabrechnung spätestens bis zum <strong>{returnDate.toLocaleDateString('de-DE')}</strong> (30 Tage) einzureichen.</li>
                <li>Nicht verwendete Beträge unverzüglich zurückzuzahlen.</li>
                <li>Bei Verzug oder Nicht-Abrechnung den vollen Betrag auf erstes Anfordern zurückzuzahlen.</li>
              </ol>
              
              <h6 className="mt-3">Haftung und Verantwortung:</h6>
              <p>
                Ich übernehme die volle Verantwortung für das erhaltene Verrechnungsgeld und hafte persönlich für:
              </p>
              <ul>
                <li>Verlust oder Diebstahl des Geldes</li>
                <li>Nicht ordnungsgemäße Verwendung</li>
                <li>Fehlende oder unvollständige Belege</li>
                <li>Verspätete Abrechnung</li>
              </ul>
              
              <h6 className="mt-3">Rechtliche Konsequenzen:</h6>
              <p>
                Mir ist bewusst, dass bei Nichteinhaltung dieser Vereinbarung arbeitsrechtliche Konsequenzen drohen können,
                einschließlich der Möglichkeit, dass die MXR Productions GmbH berechtigt ist, offene Beträge vom Gehalt
                einzubehalten oder andere rechtliche Schritte einzuleiten.
              </p>
              
              <p className="mt-3">
                <small>Diese Vereinbarung unterliegt österreichischem Recht. Gerichtsstand ist Wien.</small>
              </p>
            </div>
            
            <div className="mt-4">
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  />
                  {' '}
                  <strong>Ich habe die Bedingungen gelesen und akzeptiere sie vollumfänglich.</strong>
                </Label>
              </FormGroup>
              
              <FormGroup className="mt-3">
                <Label>Digitale Unterschrift - Bitte geben Sie Ihren Namen zur Bestätigung ein:</Label>
                <Input
                  type="text"
                  value={userNameInput}
                  onChange={(e) => setUserNameInput(e.target.value)}
                  placeholder={userName}
                />
                <small className="text-muted">
                  Geben Sie "{userName}" ein, um die Vereinbarung zu unterschreiben.
                </small>
              </FormGroup>
            </div>
          </div>
        )}
      </ModalBody>
      
      <ModalFooter>
        {step === 'form' ? (
          <>
            <Button color="secondary" onClick={toggle}>
              Abbrechen
            </Button>
            <Button 
              color="primary" 
              onClick={handleSubmitRequest}
              disabled={!formData.amount || parseFloat(formData.amount) <= 0 || loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Wird verarbeitet...
                </>
              ) : (
                activeTab === 'self' ? 'Weiter zur Vereinbarung' : 'Anfrage senden'
              )}
            </Button>
          </>
        ) : (
          <>
            <Button 
              color="secondary" 
              onClick={() => setStep('form')}
              disabled={loading}
            >
              Zurück
            </Button>
            <Button 
              color="warning" 
              onClick={handleAcceptDisclaimer}
              disabled={loading || !disclaimerAccepted || userNameInput.toLowerCase() !== userName.toLowerCase()}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Wird verarbeitet...
                </>
              ) : (
                <>
                  <RiCheckLine className="me-2" />
                  Vereinbarung unterschreiben & absenden
                </>
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default QuickMoneySpotlight;